import type { Patrol } from '@local/common-lib';
import {
  Add as AddIcon,
  Stop as FinishIcon,
  AddLocation as LocationIcon,
  GpsOff as LocationUnavailableIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import type { Theme } from '@mui/material';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { Position } from 'geojson';
import type { FC } from 'react';
import type { Contract } from 'validata';
import { isDate, isNumber, isString, maybeString, maybeTuple } from 'validata';
import { CenterLayout } from '../../../components/page-layout/index.js';
import { PatrolTimeline } from '../../../components/patrol-timeline/index.js';
import { AddIncident } from './add-incident.controller.js';

export interface AddIncidentForm {
  address?: string;
  locationNotes?: string;
  people: number;
  peopleDetails: string;
  position?: Position;
  what?: string;
  whatDetails: string;
  when: Date;
}

export const addIncidentFormContract: Contract<AddIncidentForm> = {
  address: maybeString(),
  locationNotes: maybeString(),
  people: isNumber(),
  peopleDetails: isString(),
  position: maybeTuple([isNumber(), isNumber()]),
  what: isString(),
  whatDetails: isString(),
  when: isDate(),
};

interface ViewProps<T> {
  finishingIncidentId?: string;
  locationAvailable: boolean;
  locationLoading: boolean;
  onAddLocation: () => void;
  onFinishIncident: (incident: Patrol.Incident) => void;
  patrol: T;
  refresh: () => void;
  setShowAdd: (value: boolean) => void;
  setShowFinish: (value: boolean) => void;
  showAdd: boolean;
}

export const PatrolProgressView: FC<ViewProps<Patrol.Type>> = ({
  finishingIncidentId,
  locationAvailable,
  locationLoading,
  onAddLocation,
  onFinishIncident,
  patrol,
  refresh,
  setShowAdd,
  setShowFinish,
  showAdd,
}) => {
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  // view
  return (
    <CenterLayout>
      <Collapse in={!showAdd}>
        {!showAdd ? (
          <Stack direction="column" spacing={3}>
            {!locationAvailable ? (
              <Alert
                color="warning"
                icon={<LocationUnavailableIcon />}
                onClick={() => refresh()}
                sx={{ cursor: 'pointer', mb: { md: 3, xs: 1 } }}
                variant="filled"
              >
                This page has not been granted access location. Please grant
                access in the browser settings.
              </Alert>
            ) : null}
            <Card>
              <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant={smallScreen ? 'h6' : 'h4'}>
                  Patrol in Progress
                </Typography>
                <Button
                  onClick={() => setShowFinish(true)}
                  startIcon={<FinishIcon />}
                  sx={{ alignSelf: 'end' }}
                  variant="contained"
                >
                  Finish
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader
                action={
                  <Stack direction="row" spacing={2} sx={{ mr: 1 }}>
                    <LoadingButton
                      disabled={!locationAvailable}
                      loading={locationLoading}
                      onClick={() => onAddLocation()}
                      startIcon={<LocationIcon />}
                      sx={{ alignSelf: 'stretch', flexGrow: 1 }}
                      variant="contained"
                    >
                      {!smallScreen ? 'Location' : 'Loc'}
                    </LoadingButton>
                    <Button
                      onClick={() => setShowAdd(true)}
                      startIcon={<AddIcon />}
                      sx={{ alignSelf: 'stretch', flexGrow: 1 }}
                      variant="contained"
                    >
                      {!smallScreen ? 'Activity' : 'Act'}
                    </Button>
                  </Stack>
                }
                title="Timeline"
              />
              <CardContent sx={{ display: 'flex' }}>
                <PatrolTimeline
                  finishingIncidentId={finishingIncidentId}
                  onFinishIncident={onFinishIncident}
                  patrol={patrol}
                />
              </CardContent>
            </Card>
          </Stack>
        ) : null}
      </Collapse>
      <Collapse in={showAdd}>
        {showAdd ? (
          <Stack direction="column" spacing={3}>
            {!locationAvailable ? (
              <Alert
                color="warning"
                icon={<LocationUnavailableIcon />}
                onClick={() => refresh()}
                sx={{ cursor: 'pointer', mb: { md: 3, xs: 1 } }}
                variant="filled"
              >
                This page has not been granted access location. Please grant
                access in the browser settings.
              </Alert>
            ) : null}
            <AddIncident
              onCancel={() => setShowAdd(false)}
              onDone={() => {
                setShowAdd(false);
                refresh();
              }}
              patrol={patrol}
            />
          </Stack>
        ) : null}
      </Collapse>
    </CenterLayout>
  );
};
